import * as util from "./util.js";
function quadbez(a: number, b: number, c: number, t: number): number {
  const f1 = a + (b - a) * t;
  const f2 = b + (c - b) * t;

  return f1 + (f2 - f1) * t;
}

function cubicbez(a: number, b: number, c: number, d: number, t: number): number {
  const f1 = quadbez(a, b, c, t);
  const f2 = quadbez(b, c, d, t);

  return f1 + (f2 - f1) * t;
}

function quarticbez(a: number, b: number, c: number, d: number, e: number, t: number): number {
  const f1 = cubicbez(a, b, c, d, t);
  const f2 = cubicbez(b, c, d, e, t);

  return f1 + (f2 - f1) * t;
}

const HVAL = 0;
const HNUM = 1;
const HTOT = 2;

class Histogram extends Float64Array {
  tot: number;
  min: number | undefined;
  max: number | undefined;
  size: number;

  constructor(size: number) {
    super(size * HTOT);

    this.tot = 0.0;
    this.min = undefined;
    this.max = undefined;
    this.size = size;

    this.reset();
  }

  reset() {
    this.fill(0, 0, this.length);

    this.tot = 0.0;
    this.min = this.max = undefined;
  }

  add(val: number): void {
    val = Math.min(Math.max(val, 0.0), 1.0);
    const hi = ~~(val * (this.size - 1)) * HTOT;
    const oldtot = this.tot;

    this[hi + HVAL] += val;
    this[hi + HNUM]++;

    this.tot += val;

    const f = this[hi + HVAL];

    if (this.min === undefined) {
      this.min = this.max = f;
    } else {
      this.min = Math.min(this.min * oldtot, f) / this.tot;
      this.max = Math.max(this.max! * oldtot, f) / this.tot;
    }
  }

  sample(val: number): number {
    val = Math.min(Math.max(val, 0.0), 1.0);
    const hi = ~~(val * (this.size - 1)) * HTOT;

    if (this.tot == 0.0) {
      return 0.0;
    }

    return this[hi + HVAL] / this.tot;
  }

  //normalizes result
  getBins(): number[] {
    const ret: number[] = [];

    for (let i = 0; i < this.length; i += HTOT) {
      ret.push(this[i + HVAL] / this.tot);
    }

    return ret;
  }

  //interp_penalize is just multiplied with interpolated points
  //defaults to 1.0
  finish(interp_penalize?: number): void {
    interp_penalize = interp_penalize === undefined ? 1.0 : interp_penalize;

    /*
      this.min = this.max = undefined;
      for (let i=0; i<this.length; i += HTOT) {
        let f = this[i+HVAL] / this.tot;
        
        if (this.min === undefined) {
          this.min = this.max = f;
        } else {
          this.min = Math.min(this.min, f);
          this.max = Math.max(this.max, f);
        }
      }
      
      return;
      //*/

    //interpolate gaps in histogram
    if (this.tot == 0.0) {
      //anything in histogram?
      return;
    }

    //make sure we have a first entry
    if (this[HNUM] == 0) {
      this[HVAL] = 0;
      this[HNUM] = 1;
    }

    for (let i = 0; i < this.length - HTOT; i += HTOT) {
      //we want to be inside a filled bin, next to a (possibly series of) unfilled one
      if (this[i + HNUM] == 0.0 || this[i + HTOT + HNUM] != 0) {
        continue;
      }

      const a = i;
      let b = undefined;

      for (let j = i + HTOT; j < this.length; j += HTOT) {
        if (this[j + HNUM] != 0) {
          b = j;
          break;
        }
      }

      if (b === undefined) {
        this[this.length - HTOT + HNUM] = this[a + HNUM];
        this[this.length - HTOT + HVAL] = this[a + HVAL];
        this.tot += this[a + HVAL];

        b = this.length - HTOT;
      }

      for (let j = a + HTOT; j < b; j += HTOT) {
        const t = (j - a) / (b - a);

        this[j + HVAL] = (this[a + HVAL] + (this[b + HVAL] - this[a + HVAL]) * t) * interp_penalize;
        this[j + HNUM] = (this[a + HNUM] + (this[b + HNUM] - this[a + HNUM]) * t) * interp_penalize;

        this.tot += this[j + HVAL];

        i = j;
      }
    }

    let tot = 0.0;
    for (let i = 0; i < this.length; i += HTOT) {
      tot += this[i + HVAL];
    }

    this.tot = tot;
    if (this.tot == 0.0) {
      return;
    }

    this.min = this.max = undefined;
    for (let i = 0; i < this.length; i += HTOT) {
      const f = this[i + HVAL] / this.tot;

      if (this.min === undefined) {
        this.min = this.max = f;
      } else {
        this.min = Math.min(this.min, f);
        this.max = Math.max(this.max!, f);
      }
    }
  }

  draw(canvas: HTMLCanvasElement, g: CanvasRenderingContext2D): void {
    const x = 15;
    const y = 15;
    const w = 400;
    const h = 100;

    console.log("histogram draw!");

    g.beginPath();
    g.strokeStyle = "black";
    g.fillStyle = "white";

    g.rect(x, y - 4, x + w, y + h + 8);

    g.stroke();
    g.fill();

    g.beginPath();
    g.moveTo(x, y + h);
    g.lineTo(x + w + 16, y + h);
    g.strokeStyle = "grey";
    g.stroke();

    g.beginPath();

    let x2 = x;
    const dx = w / this.size;
    let lasty2 = y + h;
    let lastx2 = x2;
    let hsum = 0.0;

    for (let i = 0; this.tot > 0 && i < this.length; i += HTOT) {
      let val = this[i + HVAL] / this.tot;
      hsum += val;

      val = 1.0 - val / this.max!;

      const y2 = y + val * h;

      if (i != 0) {
        g.moveTo(x2 - dx, y + h);
        g.lineTo(x2 - dx, y2);
        g.lineTo(x2, y2);
        g.lineTo(x2, y + h);
      }

      lasty2 = y2;
      lastx2 = x2;
      x2 += dx;
    }

    console.log("hsum", hsum);

    g.strokeStyle = "red";
    g.fillStyle = "red";

    //g.fill();
    g.stroke();
  }
}

export { Histogram };
